import React, { useEffect, useRef } from 'react';

interface SegmentContextMenuProps {
  x: number;
  y: number;
  segmentIndex: number;
  isMuted: boolean;
  onSplit: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  /** False at the ends of the timeline, where there is nowhere to move to. */
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  onMute: () => void;
  onVolumeOpen: () => void;
  onExportSegment: () => void;
  onAddCaption: () => void;
  onAddEmoji: () => void;
  onClose: () => void;
}

const SegmentContextMenu: React.FC<SegmentContextMenuProps> = ({
  x, y, segmentIndex, isMuted,
  onSplit, onMoveLeft, onMoveRight, canMoveLeft, canMoveRight,
  onDuplicate, onDelete, onMute, onVolumeOpen,
  onExportSegment, onAddCaption, onAddEmoji, onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  // Clamp position so menu stays in viewport
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(x, window.innerWidth - 220),
    top: Math.min(y, window.innerHeight - 320),
    zIndex: 1000,
  };

  return (
    <div className="segment-context-menu" style={style} ref={ref}>
      <div className="scm-header">Segment {segmentIndex + 1}</div>
      <button className="scm-item" title="Cut this segment in two at the playhead" onClick={() => { onSplit(); onClose(); }}>
        <span className="scm-icon">✂</span> Split Here
      </button>
      <button
        className="scm-item"
        title={canMoveLeft ? 'Swap this segment with the one before it' : 'Already first'}
        disabled={!canMoveLeft}
        onClick={() => { onMoveLeft(); onClose(); }}
      >
        <span className="scm-icon">◀</span> Move Left
      </button>
      <button
        className="scm-item"
        title={canMoveRight ? 'Swap this segment with the one after it' : 'Already last'}
        disabled={!canMoveRight}
        onClick={() => { onMoveRight(); onClose(); }}
      >
        <span className="scm-icon">▶</span> Move Right
      </button>
      <button className="scm-item" title="Add a copy of this segment right after it" onClick={() => { onDuplicate(); onClose(); }}>
        <span className="scm-icon">📋</span> Duplicate
      </button>
      <button className="scm-item scm-item--danger" title="Remove this segment — the clip gets shorter" onClick={() => { onDelete(); onClose(); }}>
        <span className="scm-icon">🗑</span> Delete
      </button>
      <button className="scm-item" title={isMuted ? 'Play this segment\u2019s audio again' : 'Silence this segment\u2019s audio'} onClick={() => { onMute(); onClose(); }}>
        <span className="scm-icon">{isMuted ? '🔊' : '🔇'}</span> {isMuted ? 'Unmute' : 'Mute'}
      </button>
      <button className="scm-item" title="Set how loud this segment plays" onClick={() => { onVolumeOpen(); onClose(); }}>
        <span className="scm-icon">🔊</span> Volume...
      </button>
      <div className="scm-separator" />
      <button className="scm-item" title="Export just this segment as its own video file" onClick={() => { onExportSegment(); onClose(); }}>
        <span className="scm-icon">📤</span> Export Segment
      </button>
      <button className="scm-item" title="Add caption text over this segment" onClick={() => { onAddCaption(); onClose(); }}>
        <span className="scm-icon">📝</span> Add Caption
      </button>
      <button className="scm-item" title="Place an emoji over this segment" onClick={() => { onAddEmoji(); onClose(); }}>
        <span className="scm-icon">😀</span> Add Emoji
      </button>
    </div>
  );
};

export default SegmentContextMenu;
